<template v-show="state">
  <overlay
    key="searchShelf"
    direction="fade"
    namespace="searchShelf"
    type="shelf"
    block-class="search-shelf"
    :teleport="false"
    :show-close="false"
  >
    <template #body>
      <div class="header-search">
        <div class="header-search__container">
          <ul class="header-search__list">
            <template v-if="collections.length">
              <li
                v-for="(collection, index) of collections"
                :key="`${collection.handle}-${index}`"
                class="header-search__list-item"
              >
                <a
                  :href="collection.handle"
                  class="text-body-s-regular"
                  v-html="formatCollectionTitle(collection.title)"
                />
              </li>
            </template>

            <template v-else-if="state === 'loading'">
              <li
                v-for="index of 6"
                :key="index"
                class="header-search__list-item"
                :class="$class('loading')"
              />
            </template>
          </ul>

          <div class="header-search__product-grid">
            <template v-if="products.length">
              <product-card
                v-for="(product, index) of products"
                :key="`${product.handle}-${index}`"
                orientation="horizontal"
                :product="{
                  available: product.available,
                  comingSoonDeadline: product.comingSoonDeadline,
                  featuredImage: product.featured_image,
                  price: {
                    amount: product.price * 100,
                  },
                  title: product.title,
                  type: product.type,
                  url: product.url,
                }"
                :coming-soon="comingSoon"
                :quickview="false"
              />
            </template>

            <template v-else-if="state === 'loading'">
              <product-card
                v-for="(index) of 6"
                :key="index"
                orientation="horizontal"
                :product="false"
                :quickview="false"
              />
            </template>
          </div>

          <p
            v-if="
              (products.length === 0 && collections.length === 0) &&
                state === 'loaded'
            "
            v-html="noResultsString"
          />
        </div>
      </div>
    </template>
  </overlay>
</template>

<script>

/**
 * Vue: Header search (header-search)
 * -----------------------------------------------------------------------------
 * Header search component to display search results.
 *
 */
import { mapActions, mapState } from 'vuex'

import Overlay from '~global/overlay/overlay'
import ProductCard from '~global/product-card/product-card'

import { formatProductMetafieldsResponse } from '~/format/format-product-metafields-response'
import getProductMetafieldsByHandle from '~graphql/metafield.gql'
import { debounce, fetchJsonView } from '~/helpers/general'

export default {
  name: 'HeaderSearch',

  components: {
    Overlay,
    ProductCard,
  },

  props: {
    comingSoon: {
      type: Boolean,
      default: false,
    },
    query: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      state: false,
      products: [],
      collections: [],
      controller: {},
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      settings: (state) => state.index.settings,
    }),

    /**
     * Transforms the string for no results to include the query
     * @returns {String}
     */
    noResultsString() {
      return this.$string('search.no_results_html')
        .replace('{query}', this.query)
    },
  },

  watch: {

    /**
     * Watch for query change then perform search.
     */
    query() {
      this.updateState()
      this.debounceSearch()
      this.handleSearchOverlay()
    },
  },

  mounted() {
    this.updateState(this.query)
    this.debounceSearch = debounce(this.fetchData, this.settings.throttle)
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      closeOverlay: 'overlays/close',
      openOverlay: 'overlays/open',
    }),

    /**
     * Handle the search overlay state.
     */
    handleSearchOverlay() {
      if (!this.state) {
        this.closeOverlay()

        return
      }

      this.openOverlay({
        namespace: 'searchShelf',
        ignoreDismissed: true,
      })
    },

    /**
     * Fetches the "coming_soon_deadline" metafield for each product in array.
     * @param {Array} products - Array of product objects with a 'handle'
     * property.
     * @returns {Object} - An object mapping product handles
     * to their "coming_soon_deadline" metafield value (string or null).
     */
    async fetchProductMetafields(products) {
      try {
        if (this.controller.abort) {
          this.controller.abort()
        }

        this.controller = new AbortController()
        this.state = 'loading'

        const queue = products.map((product) => {
          return cnvs.Query({
            query: getProductMetafieldsByHandle,
            signal: this.controller.signal,
            variables: {
              handle: product.handle,
              namespace: 'custom',
              key: 'coming_soon_deadline',
            },
          })
        })

        const responses = await Promise.all(queue)

        return formatProductMetafieldsResponse(
          responses,
          'custom',
          'coming_soon_deadline',
        )

      } catch (error) {
        if (error.name === 'AbortError') {
          return {}
        }

        cnvs.ReportError('Failed to load product metafields', error)
        return {}
      }
    },

    /**
     * Update state.
     */
    updateState() {
      if (this.query !== '') {
        this.state = 'loading'

        return
      }

      this.products = []
      this.collections = []
      this.state = false
    },

    /**
     * Format collection title.
     * @param {String} title - Collection title.
     */
    formatCollectionTitle(title) {
      const regexValue = new RegExp(`(${this.query})`, 'ig')

      return title.replace(regexValue, `<span class="text-body-s-bold">$1</span>`)
    },

    /**
     * Fetches data from JSON template.
     */
    async fetchData() {
      if (!this.state) {
        return
      }

      const route = window.cnvs.routes.root
      const url = `${route}search/suggest.json?q=${this.query}&resources[type]=product,collection&resources[options][fields]=title,variants.sku,product_type,variants.title`

      try {
        const data = await fetchJsonView(url)

        this.products = data.resources.results.products.slice(0, 6)
        this.collections = data.resources.results.collections.slice(0, 6)

        const metafieldData = await this.fetchProductMetafields(this.products)

        Object.entries(metafieldData).forEach(([handle, value]) => {
          const product = this.products.find((pro) => pro.handle === handle)
          if (product) {
            product.comingSoonDeadline = value
          }
        })

      } catch (error) {
        cnvs.ReportError('Failed to load products', error)

      } finally {
        this.state = 'loaded'
      }
    },
  },
}
</script>

<style lang="scss">
@import './header-search';
</style>
